import React, { useCallback, useContext, useMemo, useState } from 'react'
import { SearchResult } from '@/src/types/api-types'
import useSearchProductsQuery from '@/lib/hook/useSearchProductsQuery'
import SETTINGS from '@/src/settings'
import { useInternalRecommend } from '@/lib/hook/useInternalRecommend'
import { withOwnReason, withoutProducts } from '@/lib/recommendFilters'
import { getRecentProducts } from '@/lib/recentProducts'
import { useWishlist } from '@/lib/hook/useWishlist'
import useEnrichProducts from '@/lib/hook/useEnrichProducts'
import ProfileContext from '@/components/Context/ProfileContext'
import RecommendCard, { DEFAULT_PREVIEW_COUNT } from '@/components/Recommend/RecommendCard'

export type CardType =
  | 'trending'
  | 'bestsellers'
  | 'smart'
  | 'company-bestsellers'
  | 'also-bought'
  | 'related'
  | 'fbt'
  | 'recent'
  | 'wishlist'

type CardConfig = {
  type: CardType
  productId?: string
  productIds?: string[]
  companyId?: string
  companyName?: string
  excludeObjectIDs?: string[]
}

type Props = {
  cards?: CardConfig[]
  productId?: string
  companyId?: string
  companyName?: string
  cartProductIds?: string[]
  defaultListView?: boolean
  /** Page qui affiche les blocs, pour la mesure par emplacement */
  placement?: string
}

// Marge conservée au repli sur les Meilleures ventes, comme avant
const INTERNAL_LIMIT = 12
// Une requête par article du panier : on s'en tient aux premiers
const MAX_CART_SEEDS = 5

// Default: home variant
const DEFAULT_CARDS: CardConfig[] = [
  { type: 'trending' },
  { type: 'smart' },
  { type: 'bestsellers' },
]

const RecommendSection: React.FC<Props> = ({
  cards = DEFAULT_CARDS,
  productId,
  companyId,
  companyName,
  cartProductIds,
  defaultListView = true,
  placement = 'home',
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const { profile, loadingProfile } = useContext(ProfileContext)

  // Test A/B : le serveur applique les règles du nouveau moteur au bras B (déjà acheté retiré,
  // plafond par marque) et renvoie la liste brute au bras A. Côté blocs, A garde ses meilleures
  // ventes et ses « aussi acheté » historiques, servis par la recherche.
  // Le bras n'est connu qu'avec le profil : attendre évite de lancer des requêtes pour rien.
  const isNewEngine = profile?.recommendVariant === 'B'
  // Nouveaux blocs, servis à part du test ci-dessus : « aussi acheté » devient tous vendeurs.
  const blocksV2 = !!profile?.recommendBlocksV2
  const isHistoricEngine = !isNewEngine && !loadingProfile

  const excludedIds = useMemo(
    () =>
      Array.from(new Set([productId, ...(cartProductIds ?? [])].filter(Boolean).map(String))),
    [productId, cartProductIds]
  )
  const listExclusions = isNewEngine ? excludedIds : []

  // Resolve card configs with parent props
  const resolvedCards = useMemo(
    () =>
      cards.map(card => ({
        ...card,
        productId: card.productId || productId,
        companyId: card.companyId || companyId,
        companyName: card.companyName || companyName,
        productIds: card.productIds || (productId ? [productId] : undefined),
        excludeObjectIDs: card.excludeObjectIDs || cartProductIds,
      })),
    [cards, productId, companyId, companyName, cartProductIds]
  )

  // Data hooks — called unconditionally, enabled based on card presence
  const hasType = (type: CardType) => resolvedCards.some(c => c.type === type)

  const { products: trendingProducts, loading: trendingLoading } = useInternalRecommend({
    model: 'trending-items',
    maxRecommendations: INTERNAL_LIMIT,
    excludeObjectIDs: listExclusions,
    enabled: !loadingProfile, // Always load — used as fallback for empty cards
  })

  const { data: bestSellersData } = useSearchProductsQuery({
    query: '',
    page: 1,
    resultsPerPage: '16',
    filters: [{ label: `${SETTINGS.idFiltersFacet.recommandationId}`, value: 'Y' }],
    sortBy: 'bestsellers',
    sortOrder: 'desc',
    getFilters: false,
    excludeRecommendBrands: true,
  }, { enabled: isHistoricEngine })
  const historicBestSellers = (bestSellersData as SearchResult)?.products || []

  const companyCard = resolvedCards.find(c => c.type === 'company-bestsellers')
  const { data: companyData } = useSearchProductsQuery(
    {
      query: '',
      page: 1,
      filtersQuery: [{ label: 'company_id', value: companyCard?.companyId }],
      resultsPerPage: '16',
      sortBy: 'bestsellers',
      sortOrder: 'desc',
      getFilters: false,
      excludeRecommendBrands: true,
    },
    { enabled: isHistoricEngine && !!companyCard?.companyId }
  )
  const { products: internalCompany, loading: internalCompanyLoading } = useInternalRecommend({
    model: 'trending-items',
    companyId: companyCard?.companyId,
    maxRecommendations: INTERNAL_LIMIT,
    excludeObjectIDs: excludedIds,
    enabled: isNewEngine && !!companyCard?.companyId,
  })
  const historicCompany = (companyData as SearchResult)?.products || []

  const alsoBoughtCard = resolvedCards.find(c => c.type === 'also-bought')
  const { data: alsoBoughtData } = useSearchProductsQuery(
    {
      query: '',
      page: 1,
      alsoBoughtForProductIds: alsoBoughtCard?.productIds,
      resultsPerPage: '16',
      sortBy: 'bestsellers',
      sortOrder: 'desc',
      getFilters: false,
      excludeRecommendBrands: true,
    },
    { enabled: isHistoricEngine && !!alsoBoughtCard?.productIds?.length }
  )
  const { products: internalAlsoBought, loading: internalAlsoBoughtLoading } = useInternalRecommend({
    model: 'bought-together',
    objectIDs: alsoBoughtCard?.productIds,
    maxRecommendations: INTERNAL_LIMIT,
    excludeObjectIDs: excludedIds,
    enabled: isNewEngine && !!alsoBoughtCard?.productIds?.length,
  })
  const historicAlsoBought = (alsoBoughtData as SearchResult)?.products || []
  const { products: boughtWithProducts, loading: boughtWithLoading } = useInternalRecommend({
    model: 'bought-with',
    objectIDs: alsoBoughtCard?.productIds,
    maxRecommendations: INTERNAL_LIMIT,
    excludeObjectIDs: excludedIds,
    enabled: blocksV2 && !!alsoBoughtCard?.productIds?.length,
  })

  const relatedCard = resolvedCards.find(c => c.type === 'related')
  const { products: relatedProducts, loading: relatedLoading } = useInternalRecommend({
    model: 'related-products',
    objectIDs: relatedCard?.productId ? [relatedCard.productId] : [],
    maxRecommendations: INTERNAL_LIMIT,
    excludeObjectIDs: listExclusions,
    enabled: !loadingProfile && !!relatedCard?.productId,
  })

  const { products: fbtProducts, loading: fbtLoading } = useInternalRecommend({
    model: 'bought-together',
    objectIDs: (cartProductIds ?? []).slice(0, MAX_CART_SEEDS),
    maxRecommendations: INTERNAL_LIMIT,
    excludeObjectIDs: excludedIds,
    enabled: !loadingProfile && hasType('fbt') && (cartProductIds?.length ?? 0) > 0,
  })

  // Bras B : les meilleures ventes sont le classement du moteur, déjà lu pour les tendances.
  const bestSellerProducts = isNewEngine ? trendingProducts : historicBestSellers
  const companyProducts = isNewEngine ? internalCompany : historicCompany
  const alsoBoughtProducts = isNewEngine ? internalAlsoBought : historicAlsoBought
  const internalLoading =
    trendingLoading ||
    relatedLoading ||
    fbtLoading ||
    internalCompanyLoading ||
    internalAlsoBoughtLoading ||
    boughtWithLoading

  const { products: wishlistProducts } = useWishlist({
    enabled: hasType('wishlist') || hasType('smart'),
  })

  const recentProducts = useMemo(() => {
    if (!hasType('recent') && !hasType('smart')) return []
    const recent = getRecentProducts()
    return recent.slice(0, 12).map(item => ({
      product_id: item.product_id ?? item.slug,
      product: item.name ?? '',
      seo_name: item.slug,
      product_code: item.ean ?? '',
      price: item.base_price ?? '0',
      base_price: item.base_price ?? '0',
      brand_name: item.brand_name ?? '',
      brand_seo_name: item.brand_seo_name ?? '',
      main_pair: item.image ? { detailed: { image_path: item.image } } : null,
    }))
  }, [])

  // ── Price enrichment for light products ──
  // The search index stores a single default price, not user-group-specific pricing.
  // We batch-fetch real prices from CS-Cart via GET_PRODUCT_BY_SLUG
  // (same approach as the search bar autocomplete ProductCard).
  const lightProducts = useMemo(() => {
    const seen = new Set<string>()
    const result: any[] = []
    const internal = isNewEngine ? [...internalCompany, ...internalAlsoBought] : []
    for (const p of [...trendingProducts, ...relatedProducts, ...fbtProducts, ...recentProducts, ...internal, ...boughtWithProducts]) {
      if (p.seo_name && !seen.has(p.seo_name)) {
        seen.add(p.seo_name)
        result.push(p)
      }
    }
    return result
  }, [trendingProducts, relatedProducts, fbtProducts, recentProducts, isNewEngine, internalCompany, internalAlsoBought, boughtWithProducts])

  const { products: enrichedLightProducts } = useEnrichProducts(
    lightProducts,
    lightProducts.length > 0 && !internalLoading
  )

  const enrichedLookup = useMemo(() => {
    const map = new Map<string, any>()
    for (const p of enrichedLightProducts) {
      if (p.seo_name) map.set(p.seo_name, p)
    }
    return map
  }, [enrichedLightProducts])

  const enrichProducts = useCallback(
    (products: any[]) => products.map(p => withOwnReason(enrichedLookup.get(p.seo_name) ?? p, p)),
    [enrichedLookup]
  )

  // Smart block: wishlist → recent → trending
  const smartProducts = useMemo(() => {
    if (wishlistProducts?.length > 0) return wishlistProducts
    if (recentProducts.length > 0) return recentProducts
    return trendingProducts
  }, [wishlistProducts, recentProducts, trendingProducts])

  const smartTitle = useMemo(() => {
    if (wishlistProducts?.length > 0) return t({ id: 'web_votre_selection' })
    if (recentProducts.length > 0) return t({ id: 'web_vos_produits_recents' })
    return t({ id: 'web_trending_now' })
  }, [wishlistProducts, recentProducts])

  const smartTooltip = useMemo(() => {
    if (wishlistProducts?.length > 0) return t({ id: 'web_votre_selection_tooltip' })
    if (recentProducts.length > 0) return t({ id: 'web_vos_produits_recents_tooltip' })
    return undefined
  }, [wishlistProducts, recentProducts])

  // Fallback: if a card has no products, replace with bestsellers
  const fallback: { title: string; products: any[]; tooltip?: string; isFallback?: boolean } = {
    title: t({ id: 'web_meilleures_ventes' }),
    products: isNewEngine ? enrichProducts(bestSellerProducts) : bestSellerProducts,
    isFallback: true,
  }

  // Map card type to data
  const getCardData = (card: CardConfig) => {
    let result: { title: string; products: any[]; tooltip?: string; isFallback?: boolean }

    switch (card.type) {
      case 'trending':
        result = { title: t({ id: 'web_trending_now' }), products: enrichProducts(trendingProducts) }
        break
      case 'bestsellers':
        result = {
          title: t({ id: 'web_meilleures_ventes' }),
          products: isNewEngine ? enrichProducts(bestSellerProducts) : bestSellerProducts,
        }
        break
      case 'company-bestsellers':
        result = {
          title: `${t({ id: 'web_company_bestseller' })} ${card.companyName || ''}`,
          products: isNewEngine ? enrichProducts(companyProducts) : companyProducts,
        }
        break
      case 'also-bought':
        if (blocksV2 && boughtWithProducts.length > 0) {
          result = {
            title: t({ id: 'web_reco_bought_with_title' }),
            products: enrichProducts(boughtWithProducts),
          }
          break
        }
        result = {
          title: t({ id: 'web_nos_pharmaciens_ont_aussi_achete' }),
          products: isNewEngine ? enrichProducts(alsoBoughtProducts) : alsoBoughtProducts,
        }
        break
      case 'related':
        result = { title: t({ id: 'web_related_products' }), products: enrichProducts(relatedProducts) }
        break
      case 'fbt':
        result = { title: t({ id: 'web_frequently_bought_together' }), products: enrichProducts(fbtProducts) }
        break
      case 'recent':
        result = { title: t({ id: 'web_vos_produits_recents' }), products: enrichProducts(recentProducts) }
        break
      case 'wishlist':
        result = { title: t({ id: 'web_votre_selection' }), products: wishlistProducts || [] }
        break
      case 'smart':
        result = { title: smartTitle, products: enrichProducts(smartProducts), tooltip: smartTooltip }
        break
      default:
        result = { title: '', products: [] }
    }

    // Fallback to bestsellers if empty
    if (!result.products || result.products.length === 0) {
      return fallback
    }

    return result
  }

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index)
  }

  const isExpanded = expandedIndex !== null

  // Build cards data, deduplicate by title, limit to 3
  const cardsData = useMemo(() => {
    const seenTitles = new Set<string>()
    // Un même produit n'apparaît pas dans deux blocs de la page. Seuls les produits affichés
    // comptent : un bloc déplié masque les autres, il ne peut pas créer de doublon.
    const shownProducts: string[] = []
    const result: { index: number; card: CardConfig; title: string; products: any[]; tooltip?: string; isFallback?: boolean }[] = []

    for (let i = 0; i < resolvedCards.length && result.length < 3; i++) {
      const data = getCardData(resolvedCards[i])
      const products = withoutProducts(data.products ?? [], shownProducts)
      if (seenTitles.has(data.title) || !products?.length) continue
      seenTitles.add(data.title)
      shownProducts.push(...products.slice(0, DEFAULT_PREVIEW_COUNT).map(p => String(p.product_id)))
      result.push({ index: i, card: resolvedCards[i], ...data, products })
    }
    return result
  }, [resolvedCards, isNewEngine, trendingProducts, bestSellerProducts, companyProducts, alsoBoughtProducts, relatedProducts, fbtProducts, recentProducts, wishlistProducts, smartProducts, enrichProducts, blocksV2, boughtWithProducts])

  return (
    <div
      className={`grid gap-4 my-4 transition-all duration-300 ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}
    >
      {cardsData.map(({ index, card, title, products, tooltip, isFallback }) => {
        if (expandedIndex !== null && expandedIndex !== index) return null

        return (
          <RecommendCard
            key={`${card.type}-${index}`}
            title={title}
            products={products}
            tooltip={tooltip}
            theme={card.type}
            expanded={expandedIndex === index}
            onToggle={() => handleToggle(index)}
            defaultListView={defaultListView}
            placement={`${placement}:${card.type}${isFallback ? '-fallback' : ''}`}
          />
        )
      })}
    </div>
  )
}

export default RecommendSection
